import tokenManager from './tokenManager';
import { getCurrentOrgId, ORG_HEADER, setCurrentOrgId } from './currentOrg';

const WEB_BUILD_ID = process.env.REACT_APP_BUILD_ID || null;
export const WEB_UPDATE_RELOAD_KEY = 'cellcog_web_update_reload_at'; // shared with utils/webUpdateOnResume.js

/**
 * Setup axios interceptor for authentication
 * @param {Object} axiosInstance - The axios instance to configure
 * @returns {Object} The configured axios instance
 */
export const setupAxiosInterceptor = (axiosInstance) => {
  // Request interceptor to add auth token
  axiosInstance.interceptors.request.use(
    async (config) => {
      try {
        const token = await tokenManager.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('[AxiosInterceptor] Failed to get token:', error);
        // Continue without token rather than blocking the request
      }
      // Organizations v3 ring 0: the org this browser acts in rides every request. The server checks
      // membership itself; an explicit header set by the caller wins.
      const orgId = getCurrentOrgId();
      if (orgId && !config.headers[ORG_HEADER]) {
        config.headers[ORG_HEADER] = orgId;
      }
      // Organizations v3 ring 1: the web version gate. Production builds carry a build id; the server can refuse a
      // too-old tab with 426 web_update_required (off until a ring needs it). Dev builds send nothing.
      if (WEB_BUILD_ID) {
        config.headers['X-CellCog-Web-Build'] = WEB_BUILD_ID;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for handling auth errors
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // One-org model M2 (multi_org_v2_prd.md rev 5): a deactivation-locked account — the
      // backend rejects EVERY call with error_type=account_deactivated. Route to the dedicated
      // lock screen (which makes no API calls), once, from this shared interceptor so every
      // api instance in the app is covered.
      if (
        error.response?.status === 403 &&
        error.response?.data?.error_type === 'account_deactivated' &&
        window.location.pathname !== '/account-deactivated'
      ) {
        window.location.assign('/account-deactivated');
        return Promise.reject(error);
      }

      // Organizations v3 ring 1: the org id this tab sent is not one of the person's active orgs (stale after a spoof
      // session, a removal, a switch in another tab). Forget it and retry ONCE without it: the server then resolves the
      // person's current org itself, and the next profile load stores the right one.
      if (
        error.response?.status === 403 &&
        error.response?.data?.detail?.error_type === 'org_not_member' &&
        !originalRequest._orgRetry
      ) {
        originalRequest._orgRetry = true;
        setCurrentOrgId(null);
        if (originalRequest.headers) {
          delete originalRequest.headers[ORG_HEADER];
        }
        return axiosInstance(originalRequest);
      }

      // Organizations v3 ring 1: the server says this tab's build is too old. Reload once to fetch the new app (a
      // sessionStorage stamp stops a reload loop if the CDN still serves the old bundle for a few seconds).
      if (error.response?.status === 426 && error.response?.data?.error_type === 'web_update_required') {
        try {
          const last = Number(sessionStorage.getItem(WEB_UPDATE_RELOAD_KEY) || 0);
          if (Date.now() - last > 60000) {
            sessionStorage.setItem(WEB_UPDATE_RELOAD_KEY, String(Date.now()));
            window.location.reload();
          }
        } catch (e) {
          /* storage unavailable: never loop, just surface the error */
        }
        return Promise.reject(error);
      }

      // CEL-1148: an account already exists for this email and the session hasn't proven
      // ownership (unverified email/password credential). Route to the verify-email lock
      // screen — same shared-interceptor pattern as account_deactivated above.
      if (
        error.response?.status === 403 &&
        error.response?.data?.error_type === 'email_verification_required' &&
        window.location.pathname !== '/verify-email-required'
      ) {
        window.location.assign('/verify-email-required');
        return Promise.reject(error);
      }

      // If we get a 401 and haven't already retried
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        
        try {
          // Force token refresh
          await tokenManager.forceRefresh();
          
          // Retry the original request with new token
          const token = await tokenManager.getToken();
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          console.error('[AxiosInterceptor] Token refresh failed:', refreshError);
          // Let the 401 propagate to the application
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    }
  );
  
  return axiosInstance;
};

/**
 * Create a configured axios instance with auth interceptor
 * @param {Object} config - Axios configuration
 * @returns {Object} Configured axios instance
 */
export const createAuthenticatedAxios = (config) => {
  const axios = require('axios');
  const instance = axios.create(config);
  return setupAxiosInterceptor(instance);
};
